/**
 * HUD-style homelessness (PIT) data: county-level totals and demographic breakdowns.
 * Tables: homelessness, homelessness_by_age, homelessness_by_race, homelessness_by_gender
 *
 * Overall `percent` in DB is a fraction of total population (count/population) — rate per 1k = percent * 1000.
 * Demographic charts use PIT counts for the county; shares are within the local PIT total for that year.
 */

import { supabaseClient } from './supabase';
import { supabaseLocationService } from './supabaseLocationService';

export type HomelessnessChartView = 'rate' | 'gender' | 'race' | 'age';

export interface DemographicSlice {
  label: string;
  count: number;
  /** Share of county PIT count, 0–100 */
  shareOfPitPercent: number;
  /** When the row includes a general-population denominator */
  ratePer1000Residents: number | null;
}

export interface HomelessnessBundle {
  hasCoreData: boolean;
  countyDisplayName: string;
  /** County geofips used for queries (string) */
  countyGeofips: string;
  latestYear: number | null;
  latestRatePer1000Local: number | null;
  latestRatePer1000CA: number | null;
  trendYears: number[];
  localRatePer1000: (number | null)[];
  caRatePer1000: (number | null)[];
  latestGender: DemographicSlice[];
  latestRace: DemographicSlice[];
  latestAge: DemographicSlice[];
  fetchWarnings: string[];
}

function normGeofips(g: unknown): string {
  const s = String(g ?? '').trim();
  if (s === '') return '';
  const stripped = s.replace(/^0+/, '') || '0';
  return stripped;
}

function isCaliforniaGeofips(g: unknown): boolean {
  return normGeofips(g) === '6';
}

function geofipsVariants(g: string): (string | number)[] {
  const raw = String(g).trim();
  const stripped = raw.replace(/^0+/, '') || '0';
  const set = new Set<string | number>([raw, stripped]);
  if (/^\d+$/.test(stripped)) {
    const n = Number(stripped);
    if (Number.isFinite(n)) set.add(n);
    set.add(stripped.padStart(7, '0'));
  }
  return Array.from(set);
}

const CA_VARIANTS: (string | number)[] = [6, '6', '0000006', '00000006'];

function parseNumber(v: unknown): number | null {
  if (v == null) return null;
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  const n = Number(String(v).replace(/,/g, ''));
  return Number.isFinite(n) ? n : null;
}

/** `percent` may be 0–1 fraction (pipeline) or 0–100 */
function fractionFromPercentField(v: unknown): number | null {
  const n = parseNumber(v);
  if (n == null || n < 0) return null;
  if (n <= 1) return n;
  return n / 100;
}

function ratePer1000FromRow(row: Record<string, unknown>): number | null {
  const explicit =
    parseNumber(row.rate_per_1000) ??
    parseNumber(row.rate_per_thousand) ??
    parseNumber(row.homeless_per_1000);
  if (explicit != null && explicit >= 0) return explicit;

  const fr = fractionFromPercentField(row.percent);
  if (fr != null) return fr * 1000;

  const count = parseNumber(row.count ?? row.homeless_count);
  const pop =
    parseNumber(row.population) ??
    parseNumber(row.total_population) ??
    parseNumber(row.denominator);
  if (count != null && pop != null && pop > 0) return (count / pop) * 1000;

  return null;
}

function pickLabel(row: Record<string, unknown>, keys: string[]): string {
  for (const k of keys) {
    const v = row[k];
    if (v != null && String(v).trim() !== '') return String(v).trim();
  }
  return 'Unknown';
}

async function resolveCountyContext(locationName: string): Promise<{
  countyGeofips: string;
  countyDisplayName: string;
} | null> {
  await supabaseLocationService.initialize();
  const geofips = await supabaseLocationService.getGeofips(locationName);
  if (geofips == null) return null;

  const geoVariants = geofipsVariants(String(geofips));
  const { data: locationData, error } = await supabaseClient
    .from('population')
    .select('geofips, location_name, geography_type, county')
    .in('geofips', geoVariants)
    .limit(1)
    .maybeSingle();

  if (error || !locationData) return null;

  const row = locationData as {
    geofips?: string | number;
    location_name?: string;
    geography_type?: string;
    county?: string | null;
  };

  let countyGeofips = String(row.geofips ?? geofips);
  let countyDisplayName = row.location_name || locationName;

  if ((row.geography_type || '').trim() === 'City' && row.county) {
    const { data: countyData } = await supabaseClient
      .from('population')
      .select('geofips, location_name')
      .eq('location_name', row.county.trim())
      .eq('geography_type', 'County')
      .limit(1)
      .maybeSingle();

    if (countyData) {
      const c = countyData as { geofips?: string; location_name?: string };
      if (c.geofips) countyGeofips = String(c.geofips);
      if (c.location_name) countyDisplayName = c.location_name;
    }
  }

  return { countyGeofips, countyDisplayName };
}

function buildDemographicSlices(rows: Record<string, unknown>[], labelKeys: string[]): DemographicSlice[] {
  const years = rows
    .map((r) => parseNumber(r.year))
    .filter((y): y is number => y != null && y > 0);
  if (!years.length) return [];
  const uniqueYears = Array.from(new Set(years)).sort((a, b) => b - a);

  for (const latestY of uniqueYears) {
    const yearRows = rows.filter((r) => parseNumber(r.year) === latestY);
    const rawCounts = yearRows.map((r) => ({
      label: pickLabel(r, labelKeys),
      count: parseNumber(r.count ?? r.homeless_count ?? r.experiencing_homeless_count) ?? 0,
      pop: parseNumber(r.population ?? r.total_population ?? r.denominator),
    }));

    const merged = new Map<string, { count: number; pop: number | null }>();
    for (const x of rawCounts) {
      const low = x.label.toLowerCase();
      if (low === 'total' || low === 'all' || x.count <= 0) continue;
      const cur = merged.get(x.label) ?? { count: 0, pop: null as number | null };
      cur.count += x.count;
      if (x.pop != null && x.pop > 0) {
        cur.pop = (cur.pop ?? 0) + x.pop;
      }
      merged.set(x.label, cur);
    }

    const total = Array.from(merged.values()).reduce((s, x) => s + x.count, 0);
    if (total <= 0) continue;

    const slices = Array.from(merged.entries())
      .map(([label, v]) => ({
        label,
        count: v.count,
        shareOfPitPercent: (v.count / total) * 100,
        ratePer1000Residents:
          v.pop != null && v.pop > 0 ? (v.count / v.pop) * 1000 : null,
      }))
      .sort((a, b) => b.count - a.count);

    if (slices.length) return slices;
  }

  return [];
}

export async function loadHomelessnessBundle(locationName: string): Promise<HomelessnessBundle> {
  const warnings: string[] = [];
  const empty = (): HomelessnessBundle => ({
    hasCoreData: false,
    countyDisplayName: locationName,
    countyGeofips: '',
    latestYear: null,
    latestRatePer1000Local: null,
    latestRatePer1000CA: null,
    trendYears: [],
    localRatePer1000: [],
    caRatePer1000: [],
    latestGender: [],
    latestRace: [],
    latestAge: [],
    fetchWarnings: warnings,
  });

  const ctx = await resolveCountyContext(locationName);
  if (!ctx) {
    warnings.push('Could not resolve county for this location.');
    return empty();
  }

  const countyVars = geofipsVariants(ctx.countyGeofips);
  const geoList = Array.from(new Set([...countyVars, ...CA_VARIANTS]));

  const selectCore = 'geofips, year, percent, count, location_name';

  const [coreRes, ageRes, raceRes, genderRes] = await Promise.all([
    supabaseClient.from('homelessness').select(selectCore).in('geofips', geoList).order('year', { ascending: true }),
    supabaseClient.from('homelessness_by_age').select('*').in('geofips', countyVars).order('year', { ascending: true }),
    supabaseClient.from('homelessness_by_race').select('*').in('geofips', countyVars).order('year', { ascending: true }),
    supabaseClient.from('homelessness_by_gender').select('*').in('geofips', countyVars).order('year', { ascending: true }),
  ]);

  if (coreRes.error) {
    const msg = coreRes.error.message || String(coreRes.error);
    if (!/does not exist|schema cache/i.test(msg)) warnings.push(`homelessness: ${msg}`);
    return { ...empty(), countyDisplayName: ctx.countyDisplayName, countyGeofips: ctx.countyGeofips };
  }

  const allCore = (coreRes.data || []) as Record<string, unknown>[];
  const countyNorm = normGeofips(ctx.countyGeofips);
  const localRows = allCore.filter((r) => normGeofips(r.geofips) === countyNorm);
  const caRows = allCore.filter((r) => isCaliforniaGeofips(r.geofips));

  if (localRows.length === 0) {
    warnings.push('No rows in homelessness for this county geofips.');
    return { ...empty(), countyDisplayName: ctx.countyDisplayName, countyGeofips: ctx.countyGeofips };
  }

  const yearSet = new Set<number>();
  for (const r of localRows) {
    const y = parseNumber(r.year);
    if (y != null) yearSet.add(y);
  }
  const trendYears = Array.from(yearSet).sort((a, b) => a - b);

  const localRatePer1000 = trendYears.map((y) => {
    const row = localRows.find((r) => parseNumber(r.year) === y);
    return row ? ratePer1000FromRow(row) : null;
  });

  const caRatePer1000 = trendYears.map((y) => {
    const row = caRows.find((r) => parseNumber(r.year) === y);
    return row ? ratePer1000FromRow(row) : null;
  });

  const lastY = trendYears[trendYears.length - 1];
  const lastLocal = localRows.find((r) => parseNumber(r.year) === lastY);
  const lastCa = caRows.find((r) => parseNumber(r.year) === lastY);

  const ageRows = (ageRes.data || []) as Record<string, unknown>[];
  const raceRows = (raceRes.data || []) as Record<string, unknown>[];
  const genderRows = (genderRes.data || []) as Record<string, unknown>[];

  if (ageRes.error && !/does not exist|schema cache/i.test(ageRes.error.message || '')) {
    warnings.push(`homelessness_by_age: ${ageRes.error.message}`);
  }
  if (raceRes.error && !/does not exist|schema cache/i.test(raceRes.error.message || '')) {
    warnings.push(`homelessness_by_race: ${raceRes.error.message}`);
  }
  if (genderRes.error && !/does not exist|schema cache/i.test(genderRes.error.message || '')) {
    warnings.push(`homelessness_by_gender: ${genderRes.error.message}`);
  }

  return {
    hasCoreData: true,
    countyDisplayName: ctx.countyDisplayName,
    countyGeofips: ctx.countyGeofips,
    latestYear: lastY,
    latestRatePer1000Local: lastLocal ? ratePer1000FromRow(lastLocal) : null,
    latestRatePer1000CA: lastCa ? ratePer1000FromRow(lastCa) : null,
    trendYears,
    localRatePer1000,
    caRatePer1000,
    latestGender: buildDemographicSlices(genderRows, ['gender', 'sex']),
    latestRace: buildDemographicSlices(raceRows, ['race_ethnicity', 'race', 'race_ethnicity_label']),
    latestAge: buildDemographicSlices(ageRows, ['age', 'age_group', 'age_bracket']),
    fetchWarnings: warnings,
  };
}

export function buildHomelessnessOverviewNarrative(locationName: string, b: HomelessnessBundle): string {
  if (!b.hasCoreData || b.latestYear == null) {
    return `Point-in-time homelessness data is not available for ${locationName} (county-level HUD-style series). Ensure Supabase tables homelessness and related breakdowns are populated.`;
  }
  const local = b.latestRatePer1000Local;
  const ca = b.latestRatePer1000CA;
  const loc = b.countyDisplayName || locationName;
  let s = `In ${b.latestYear}, ${loc} had an estimated `;
  s +=
    local != null
      ? `${local.toFixed(1)} people experiencing homelessness per 1,000 residents`
      : 'homelessness data (rate could not be computed from percent/count)';
  if (ca != null && local != null) {
    s += `, compared with ${ca.toFixed(1)} per 1,000 in California overall`;
  }
  s += '. Demographic charts show how the local PIT count is distributed by gender, race/ethnicity, and age where those breakdowns exist.';
  return s;
}
